import { useState } from 'react';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

const services = [
  {
    id: 'roof',
    icon: 'ri-home-3-line',
    title: 'Roof Damage Claims',
    description:
      'From missing shingles to full structural collapse, we document and fight for complete roof replacement coverage. Insurance companies routinely undervalue roof damage — we make sure you get fair compensation.',
    image:
      'https://public.readdy.ai/ai/img_res/2f0cf495acd5f5d93af5d04646fc8ea2.jpg',
    cta: 'Get Roof Claim Help',
  },
  {
    id: 'hurricane',
    icon: 'ri-typhoon-line',
    title: 'Hurricane & Storm Claims',
    description:
      'Hurricane and storm claims are among the most complex and frequently underpaid. Our public adjusters specialize in catastrophic storm damage, ensuring every affected area of your property is documented and compensated.',
    image:
      'https://readdy.ai/api/search-image?query=house%20exterior%20with%20hurricane%20storm%20damage%20debris%20scattered%20around%20yard%20broken%20windows%20damaged%20siding%20and%20roof%20damage%2C%20dramatic%20overcast%20sky%2C%20wide%20angle%20property%20damage%20assessment%20photography&width=800&height=600&seq=srv002&orientation=landscape',
    cta: 'Get Storm Claim Help',
  },
  {
    id: 'water',
    icon: 'ri-drop-line',
    title: 'Water Damage Claims',
    description:
      'Water damage spreads fast and the true cost is often hidden inside walls, floors, and ceilings. We use advanced moisture detection to uncover all damage so your claim covers complete restoration — not just surface repairs.',
    image:
      'https://readdy.ai/api/search-image?query=interior%20of%20house%20with%20severe%20water%20damage%20flooded%20living%20room%20with%20water%20stains%20on%20walls%20buckled%20hardwood%20floors%20and%20water-damaged%20ceiling%20tiles%2C%20realistic%20property%20damage%20documentation%20photography&width=800&height=600&seq=srv003&orientation=landscape',
    cta: 'Get Water Claim Help',
  },
  {
    id: 'fire',
    icon: 'ri-fire-line',
    title: 'Fire & Smoke Damage Claims',
    description:
      'Fire damage claims are among the most devastating and hardest to fully recover from without expert help. We document structural damage, smoke and soot infiltration, and all contents losses to ensure your claim covers complete rebuilding and restoration — not just what the insurance company wants to pay.',
    image:
      'https://readdy.ai/api/search-image?query=residential%20house%20interior%20with%20severe%20fire%20and%20smoke%20damage%2C%20charred%20walls%20blackened%20ceiling%20scorched%20furniture%20debris%2C%20realistic%20post-fire%20property%20damage%20assessment%20photography%20with%20dramatic%20light%20coming%20through%20damaged%20structure%2C%20neutral%20plain%20background%20outside&width=800&height=600&seq=srv005&orientation=landscape',
    cta: 'Get Fire Claim Help',
  },
  {
    id: 'denied',
    icon: 'ri-file-damage-line',
    title: 'Denied & Underpaid Claims',
    description:
      "Insurance companies deny and underpay claims every day. If your claim was denied, delayed, or you received a settlement far below the actual damage, we reopen and renegotiate your claim to get you what you're owed.",
    image:
      'https://readdy.ai/api/search-image?query=denied%20insurance%20claim%20documents%20on%20desk%20with%20rejection%20stamp%20and%20frustrated%20homeowner%20reviewing%20paperwork%2C%20professional%20business%20photography%20with%20office%20background%20showing%20insurance%20policy%20documents&width=800&height=600&seq=srv004&orientation=landscape',
    cta: 'Fight Your Denied Claim',
  },
];

export default function ServicesSection() {
  const [activeId, setActiveId] = useState('roof');
  const { ref, isVisible } = useScrollAnimation();
  const active = services.find((s) => s.id === activeId) || services[0];

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="services" className="bg-[#0F1927] py-24 px-6" ref={ref}>
      <div className="max-w-7xl mx-auto">
        <div
          className={`mb-14 transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <div className="inline-flex items-center gap-2 bg-[#1B78D4]/15 border border-[#1B78D4]/40 rounded-full px-4 py-1.5 mb-4">
            <span className="text-[#5BA8E5] text-sm font-semibold">OUR SERVICES</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-extrabold text-white mb-4 max-w-2xl">
            We Handle Every Type of Property Damage Claim
          </h2>
          <p className="text-gray-400 text-lg max-w-xl">
            No matter the type of damage, our expert public adjusters fight to maximize your insurance settlement.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
          <div className="flex flex-col gap-3">
            {services.map((service, i) => (
              <button
                key={service.id}
                onClick={() => setActiveId(service.id)}
                className={`text-left rounded-xl p-5 transition-all duration-300 cursor-pointer border ${
                  activeId === service.id
                    ? 'bg-[#1B78D4]/15 border-[#1B78D4]'
                    : 'bg-white/5 border-white/10 hover:bg-white/10'
                } ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-6'}`}
                style={{ transitionDelay: `${i * 80}ms` }}
              >
                <div className="flex items-start gap-4">
                  <div
                    className={`w-12 h-12 flex items-center justify-center rounded-lg flex-shrink-0 ${
                      activeId === service.id ? 'bg-[#1B78D4] text-white' : 'bg-white/10 text-[#5BA8E5]'
                    }`}
                  >
                    <i className={`${service.icon} text-xl`} />
                  </div>
                  <div className="flex-1">
                    <div
                      className={`font-bold text-base mb-1 ${
                        activeId === service.id ? 'text-[#5BA8E5]' : 'text-white'
                      }`}
                    >
                      {service.title}
                    </div>
                    {activeId === service.id && (
                      <p className="text-gray-300 text-sm leading-relaxed">{service.description}</p>
                    )}
                  </div>
                  <i
                    className={`ri-arrow-right-line text-lg flex-shrink-0 ${
                      activeId === service.id ? 'text-[#5BA8E5]' : 'text-white/30'
                    }`}
                  />
                </div>
              </button>
            ))}

            <button
              onClick={() => scrollTo('booking')}
              className="mt-4 bg-[#1B78D4] hover:bg-[#1560B8] text-white font-bold px-8 py-4 rounded-lg transition-all duration-200 flex items-center gap-2 cursor-pointer whitespace-nowrap w-fit"
            >
              <i className="ri-calendar-check-line" />
              Get a Free Claim Review
            </button>
          </div>

          <div
            className={`relative rounded-2xl overflow-hidden transition-all duration-700 ${
              isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-6'
            }`}
          >
            <div className="w-full h-96 lg:h-[480px]">
              <img
                key={active.id}
                src={active.image}
                alt={active.title}
                className="w-full h-full object-cover object-top"
              />
            </div>
            <div className="absolute inset-0 bg-gradient-to-t from-[#0F1927]/80 via-transparent to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 p-6">
              <div className="text-[#5BA8E5] font-bold text-lg mb-2">{active.title}</div>
              <button
                onClick={() => scrollTo('booking')}
                className="bg-[#1B78D4] hover:bg-[#1560B8] text-white font-bold text-sm px-6 py-2.5 rounded-lg transition-all duration-200 cursor-pointer whitespace-nowrap"
              >
                {active.cta} &rarr;
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
